import { Command } from "commander";
import chalk from "chalk";
import { promptSelect } from "../utils/prompt.js";
import { PrebuildEngine } from "../prebuild/prebuildEngine.js";
import { ExpoPrebuildStrategy } from "../prebuild/expoPrebuildStrategy.js";
import { ReactNativePrebuildStrategy } from "../prebuild/reactNativePrebuildStrategy.js";
import { directoryExists } from "../utils/files.js";
import path from "path";
import { generateDiagnosticBundle } from "../diagnostics/bundleGenerator.js";

export const prebuildCommand = new Command("prebuild")
  .description("Generate native project directories for Expo or React Native CLI")
  .argument("[platform]", "Target platform ('ios', 'android', or 'all')", "all")
  .option("--dry-run", "Show planned actions without executing them")
  .option("--json", "Output results in JSON format")
  .option("--force", "Force regeneration of native directories")
  .action(async (platformArg: string, options: { dryRun?: boolean; json?: boolean; force?: boolean }) => {
    const cwd = process.cwd();
    const platform = (platformArg === "ios" || platformArg === "android") ? platformArg : "all";

    const iosExists = directoryExists(path.join(cwd, "ios"));
    const androidExists = directoryExists(path.join(cwd, "android"));
    const anyExists = (platform === "ios" && iosExists) || 
                      (platform === "android" && androidExists) || 
                      (platform === "all" && (iosExists || androidExists));

    if (anyExists) {
      if (!options.force) {
        if (options.json) {
          console.log(JSON.stringify({ error: "Native projects already generated. Use --force to regenerate." }, null, 2));
        } else {
          console.log();
          console.log(chalk.red("✖ Native projects already generated. Use --force to regenerate."));
          console.log();
        }
        process.exit(1);
        return;
      }

      // Force confirmation in interactive TTY
      if (process.stdout.isTTY) {
        const confirm = await promptSelect(
          "Native projects already exist and will be overwritten. Do you want to continue?",
          ["Yes", "No"]
        );
        if (confirm !== "Yes") {
          if (options.json) {
            console.log(JSON.stringify({ aborted: true, message: "Prebuild aborted by user." }, null, 2));
          } else {
            console.log();
            console.log(chalk.yellow("Prebuild aborted."));
            console.log();
          }
          process.exit(0);
          return;
        }
      }
    }

    const engine = new PrebuildEngine();
    engine.registerStrategy(new ExpoPrebuildStrategy());
    engine.registerStrategy(new ReactNativePrebuildStrategy());

    try {
      const result = await engine.runPrebuild(cwd, platform, {
        dryRun: options.dryRun,
        force: options.force,
        json: options.json,
        verbose: !options.json,
      });

      if (options.json) {
        console.log(JSON.stringify(result, null, 2));
      } else {
        const errors = result.results.filter(r => !r.success);
        if (errors.length > 0) {
          process.exit(1);
        } else {
          process.exit(0);
        }
      }
    } catch (err: any) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      let reportPath = "";
      try {
        reportPath = await generateDiagnosticBundle("prebuild", errorObj);
      } catch {}

      if (options.json) {
        console.log(JSON.stringify({ 
          error: errorObj.message,
          diagnosticBundle: reportPath 
        }, null, 2));
      } else {
        console.log();
        console.log(chalk.red(`✖ Prebuild failed: ${errorObj.message}`));
        if (reportPath) {
          console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
        }
        console.log();
      }
      process.exit(1);
    }
  });
